<h4>What this screen is for</h4>
<p>
    Reviewing the demand forecast and overriding it where you disagree. One row per
    service, date, journey and class. When you are done, Re-Optimize pushes the
    revised forecast through the optimiser.
</p>

<h4>Charts</h4>
<ul>
    <li>
        <strong>Demand Forecast Chart</strong> &mdash; booked demand, forecast demand and
        any override stacked as columns, with allocation as a step line and yield against
        last year. Clicking a point drills down a level: weekly to daily, daily to train.
        The breadcrumb above takes you back up.
    </li>
    <li>
        <strong>Performance Trends</strong> &mdash; four tabs. <em>Booking Curve</em>
        compares actual bookings against the forecast and the average curve for one
        departure date, chosen in the dropdown. <em>Bookings</em>, <em>Revenue</em> and
        <em>Yield</em> show the equivalent trends with pacing underneath.
    </li>
</ul>
<p>
    On the Revenue tab the projected bar is the <em>remaining</em> revenue stacked on
    top of booked, so the two together make the projected total.
</p>

<h4>The grid</h4>
<p>
    Use <strong>Metrics:</strong> to add or remove optional columns.
    <strong>Fcst Conf</strong> is the model's confidence, shown as an icon rather than
    a number: one bar for low, two for medium, three for high.
    <strong>Fcst Rem Dmd</strong> already accounts for any override on the row.
</p>
<p>
    Hovering a cell that contains a service code shows that train's departure time
    and origin&ndash;destination.
</p>

<h4>Overriding the forecast</h4>
<p>
    Click a value in the <strong>Fcst Total Dmd</strong> column &mdash; it is the only
    clickable cell &mdash; to open the override dialog for that row. Enter either a
    number or a percentage; typing a percentage fills in the resulting number so you
    can see what you are about to save.
</p>
<ul>
    <li><strong>Save</strong> writes immediately. There is no confirmation step.</li>
    <li><strong>Revert Forecast</strong> removes the override and restores the system value.</li>
    <li>
        To override many rows at once, tick them and use <strong>Bulk Overrides</strong>.
        The header checkbox selects every row in the result, not just the visible page.
    </li>
</ul>
<p>
    An overridden row shows the override in <strong>bold red</strong> in the
    Fcst Total Dmd column, so that column shows the system forecast on some rows and
    your override on others.
</p>

<h4>Re-Optimize</h4>
<p>
    Sends the selected markets for re-optimisation. While one is running, the
    override dialogs are blocked and you will get a browser message saying so.
</p>
<p>
    Before it starts, you are asked what should happen to the results.
    <strong>Send Automatically</strong> queues the send to the reservation system as
    soon as the re-optimisation finishes, with no review step. <strong>Allow
    Review</strong> leaves the results in FareVantage &mdash; they go with the next
    scheduled run unless you send them sooner. Cancel starts no re-optimisation at
    all.
</p>
<p>
    That review path is the button beside Re-Optimize. It reads <strong>Review &amp;
    Send</strong> with a count of affected journeys once a completed re-optimisation
    is waiting, and is otherwise disabled with a tooltip saying why. It opens the same
    review-then-send dialog as the Inventory workbench, showing the limits exactly as
    they will reach RARS; progress then arrives in the notification bell.
</p>

<h4>Worth knowing</h4>
<ul>
    <li>
        <strong>Sort the grid before applying a percentage override and the percentage
        may be applied to the override rather than the system forecast</strong>, which
        compounds it. If you have sorted, use the numeric field instead.
    </li>
    <li>
        <strong>A bulk override cannot take a negative percentage.</strong> To reduce a
        forecast across many rows, enter an absolute number instead.
    </li>
    <li>
        <strong>Export View exports every column and every row</strong> in the result
        set, not the columns you have chosen or the page you are looking at. It includes
        a second sheet listing the filters used.
    </li>
    <li>
        Your <strong>Metrics:</strong> selection does not survive a browser refresh, and
        loading a saved filter view replaces it.
    </li>
    <li>
        On the Booking Curve, the actual and forecast lines meet rather than overlap
        &mdash; forecast is only drawn for days with no actuals yet.
    </li>
    <li>
        Depending on your operator's settings, journeys with nothing bookable may be
        hidden from the grid entirely.
    </li>
</ul>
